import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { PalantirConfirmDialogService, PalantirToastService } from '@flux/design-system';
import {
  AgentApprovalEventKind,
  AgentApprovalsStreamGQL,
  type AgentApprovalsStreamSubscription,
  AgentKindEnum,
  AgentStatusEnum,
  AgentsGQL,
  ApproveAgentApprovalGQL,
  ChatMessagesGQL,
  ChatSessionsGQL,
  ChatStreamGQL,
  type ChatStreamSubscription,
  DeleteChatSessionGQL,
  EndChatSessionGQL,
  ExecuteChatComponentActionGQL,
  RegenerateMessageGQL,
  RejectAgentApprovalGQL,
  RenameChatSessionGQL,
  SendChatMessageGQL,
  type SendMessageInput,
  StopMessageGQL,
} from '@generated/graphql';
import { FluxErrorCodes } from '@flux/errors';
import { Subscription, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
  ChatAction,
  ChatActionStatus,
  ChatAgent,
  ChatApproval,
  ChatCitation,
  ChatComponent,
  ChatMessage,
  ChatSession,
  ChatThinkingStep,
} from '../models/chat.model';
import { readReasoningTrail } from '../utils/reasoning-trail';
import { sanitizeAssistantChatContent } from '../utils/sanitize-assistant-content';
import { RealtimeService } from '@core/services/realtime/realtime.service';
import { AuthService } from '@core/services/auth/auth.service';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { FeatureFlagsEnum } from '@/generated/featureFlags.enum';
import type { FluidPrefixType } from '@flux/core/client';

type ChatStreamEvent = NonNullable<ChatStreamSubscription['chatStream']>;
type ApprovalStreamEvent = NonNullable<AgentApprovalsStreamSubscription['agentApprovalsStream']>;

/**
 * Resultado de `executeChatComponentAction` (contrato `ChatComponentActionResultEntity`).
 * Lido por shape explícito — os tipos gerados são regenerados via `generate:graphql`
 * e o campo é opcional aqui para desacoplar a leitura da geração.
 */
interface ChatComponentActionResult {
  outcome?: string;
  errorMessage?: string | null;
  approvalRequestId?: string | null;
}

interface RawMessageNode {
  id: string;
  role: string;
  content: string;
  status: string;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  sources?: readonly RawSource[];
  components?: readonly RawComponent[];
}

interface RawSource {
  id: string;
  title?: string | null;
  uri?: string | null;
  sourceType?: string | null;
  score?: number | null;
}

interface RawComponent {
  componentId: string;
  componentType: string;
  instanceVersion: number;
  generatedAt?: string | null;
  data: unknown;
  presentation?: unknown;
  actions?: unknown[] | null;
  fallbackText: string;
}

/**
 * Estado e operações da conversa Iara — **feature-scoped** (provido em `chatRoutes`),
 * fiel ao backend (`@flux/iara-core` → `chatStream`). Sem singleton de app: cada
 * entrada na superfície começa limpa. Leak-safe: toda subscription é guardada e
 * cancelada (antes de abrir nova + em `DestroyRef.onDestroy`), e o `chatStream`/
 * `agentApprovalsStream` usam `fetchPolicy: 'no-cache'` (sem retenção no InMemoryCache).
 *
 * Ciclo de vida da assinatura no socket (chat-over-socket v2): a fluid v2
 * por-mensagem é assinada quando um turno começa a transmitir — `send`,
 * `regenerate`, retomada de aprovação e `openSession` de uma sessão com turno
 * pendente (inclusive em deep-link via URL, pois a `chat-page` chama `openSession`
 * no efeito do param de rota) — e cancelada em `cancelStream` (troca de sessão,
 * fim do turno e `DestroyRef.onDestroy`). Como o serviço é route-scoped, SAIR da
 * rota `/chat` destrói o serviço → `onDestroy` → `cancelStream` →
 * `unsubscribe(fluid)`; a assinatura nunca sobrevive à navegação para fora.
 */
const SESSIONS_PAGE_SIZE = 20;

/**
 * Reconexões consecutivas do `chatStream` SEM receber nenhum evento. Transporte
 * quebrado de verdade falha rápido; um stream vivo zera o contador a cada evento
 * recebido (catch-up incluso), então geração longa legítima nunca esgota o limite.
 */
const MAX_STREAM_RECONNECTS = 3;
const RECONNECT_BASE_DELAY_MS = 1_000;
/** Após este tempo de streaming, mostra o convite "pode continuar usando o Flux". */
const LONG_TURN_HINT_AFTER_MS = 15_000;

@Injectable()
export class ChatService {
  private readonly chatSessionsGql = inject(ChatSessionsGQL);
  private readonly chatMessagesGql = inject(ChatMessagesGQL);
  private readonly sendChatMessageGql = inject(SendChatMessageGQL);
  private readonly regenerateMessageGql = inject(RegenerateMessageGQL);
  private readonly renameChatSessionGql = inject(RenameChatSessionGQL);
  private readonly deleteChatSessionGql = inject(DeleteChatSessionGQL);
  private readonly endChatSessionGql = inject(EndChatSessionGQL);
  private readonly chatStreamGql = inject(ChatStreamGQL);
  private readonly agentsGql = inject(AgentsGQL);
  private readonly approveGql = inject(ApproveAgentApprovalGQL);
  private readonly rejectGql = inject(RejectAgentApprovalGQL);
  private readonly approvalsStreamGql = inject(AgentApprovalsStreamGQL);
  private readonly executeActionGql = inject(ExecuteChatComponentActionGQL);
  private readonly stopMessageGql = inject(StopMessageGQL);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(PalantirToastService);
  private readonly confirmDialog = inject(PalantirConfirmDialogService);
  private readonly realtimeService = inject(RealtimeService);
  private readonly authService = inject(AuthService);
  private readonly featureFlagService = inject(FeatureFlagService);

  public readonly sessions = signal<ChatSession[]>([]);
  /** Termo de busca de sessões (server-side). Vazio = todas. */
  public readonly sessionSearch = signal('');
  /** Há mais páginas de sessões a carregar (alimenta o infinite scroll). */
  public readonly sessionsHasMore = signal(false);
  /** Próxima página de sessões em carregamento. */
  public readonly sessionsLoadingMore = signal(false);
  public readonly messages = signal<ChatMessage[]>([]);
  public readonly agents = signal<ChatAgent[]>([]);
  public readonly activeSessionId = signal<string | null>(null);
  public readonly activeAgentId = signal<string | null>(null);
  public readonly isStreaming = signal(false);
  /** Início do turno em andamento (alimenta o cronômetro do reasoning). */
  public readonly turnStartedAt = signal<Date | null>(null);
  /** Convite "pode continuar usando o Flux" (liga após LONG_TURN_HINT_AFTER_MS). */
  public readonly longTurnHintVisible = signal(false);
  public readonly streamingText = signal<string>('');
  public readonly streamingThinking = signal<ChatThinkingStep[]>([]);
  /** Narrativa de raciocínio (chain-of-thought) acumulada do turno em andamento. */
  public readonly streamingReasoning = signal<string>('');
  public readonly pendingApprovals = signal<ChatApproval[]>([]);
  public readonly isLoadingSession = signal(false);

  /**
   * Estado runtime das ações `tool_call` por componente: `componentId` →
   * (`actionId` → status). Alimenta a UI (botão executando/aprovação/feito);
   * não é persistido — some ao trocar de sessão (junto com as mensagens).
   */
  public readonly actionStates = signal<Record<string, Record<string, ChatActionStatus>>>({});

  /**
   * Cota de créditos da Iara esgotada → bloqueia o uso e mostra o CTA de upgrade.
   * Ativado quando `send`/`regenerate` retornam o erro de limite (`iara-credits/limit-reached`
   * para a conta, `iara-credits/channel-limit-reached` para o teto do canal). Persiste no
   * ciclo (limpa em reload), pois é estado de conta — não de sessão.
   */
  public readonly creditLimitReached = signal(false);

  /** Detalhe do bloqueio: escopo (conta vs canal) + data da próxima renovação, vindos do erro. */
  public readonly creditLimitInfo = signal<{
    renewalDate: string | null;
    scope: 'account' | 'channel';
  } | null>(null);

  public readonly conversationTitle = computed(() => {
    const id = this.activeSessionId();
    if (!id) return null;
    return this.sessions().find((session) => session.id === id)?.title ?? null;
  });

  public readonly activeAgent = computed(() => {
    const id = this.activeAgentId();
    return id ? (this.agents().find((agent) => agent.id === id) ?? null) : null;
  });

  private streamSub: Subscription | null = null;
  private approvalsSub: Subscription | null = null;
  private sessionsInFlight = false;
  /** Offset (skip) da próxima página de sessões. */
  private sessionsSkip = 0;
  /** Total conhecido de sessões para o filtro/busca atual. */
  private sessionsTotalCount = 0;
  private accumulated = '';
  private accumulatedReasoning = '';
  private thinkingSteps: ChatThinkingStep[] = [];
  /** Componentes emitidos no turno em andamento, indexados por componentId. */
  private readonly streamingComponents = new Map<string, ChatComponent>();
  private streamingMessageId: string | null = null;
  /** O turno em andamento já recebeu um evento terminal (`done`/`error`)? */
  private receivedTerminal = false;
  /** Reconexões consecutivas do stream sem nenhum evento recebido. */
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private longTurnHintTimer: ReturnType<typeof setTimeout> | null = null;
  /** Subscription ao socket (chat-over-socket v2), em paralelo à do Apollo. */
  private socketStreamSub: Subscription | null = null;
  /** Fluid v2 por-mensagem assinada no socket no turno em andamento. */
  private subscribedMessageFluid: FluidPrefixType | null = null;
  /**
   * Maior `seq` já aplicado no turno. As duas fontes (socket e Apollo) carregam o
   * MESMO `seq` por frame (carimbado uma vez no servidor dono), mas são
   * transportes independentes — um pode entregar antes/depois do outro. Aplicamos
   * só `seq` ESTRITAMENTE crescente: isso dedupa (igual) E descarta frames
   * atrasados de `seq` menor — sem isso um token de `content` ABSOLUTO chegando
   * fora de ordem rolaria o texto visível para trás, e tool/reasoning entrariam
   * desordenados. Seguro: cada transporte é ordenado e o catch-up do Apollo
   * inclui todos os frames não-token, então um `seq` menor que chega depois é
   * sempre um duplicado (token absoluto já superado). Frames sem `seq` (replay/
   * reconcile do servidor) são sempre aplicados.
   */
  private lastAppliedSeq = -1;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.streamSub?.unsubscribe();
      this.approvalsSub?.unsubscribe();
      this.cancelStream();
      this.clearLongTurnHint();
    });

    void this.loadSessions();
    void this.loadAgents();
    this.subscribeToApprovals();
  }

  // ── leitura ────────────────────────────────────────────────────────────────

  /**
   * (Re)carrega a primeira página de sessões, preservando o termo de busca atual
   * (`sessionSearch`). Usado no boot, reconexão e após criar/concluir um turno.
   * Reseta a paginação do infinite scroll.
   */
  public async loadSessions(): Promise<void> {
    if (this.sessionsInFlight) return;
    this.sessionsInFlight = true;
    try {
      const page = await this.fetchSessionsPage(0);
      this.sessions.set(page.sessions);
      this.sessionsTotalCount = page.totalCount;
      this.sessionsSkip = page.sessions.length;
      this.sessionsHasMore.set(this.sessions().length < page.totalCount);
    } finally {
      this.sessionsInFlight = false;
    }
  }

  /**
   * Aplica um novo termo de busca (server-side) e recarrega a primeira página.
   * O `chat-page` faz o debounce antes de chamar.
   */
  public async searchSessions(term: string): Promise<void> {
    this.sessionSearch.set(term.trim());
    await this.loadSessions();
  }

  /**
   * Carrega a próxima página de sessões e a anexa à lista (infinite scroll).
   * No-op quando não há mais páginas ou já há um carregamento em andamento.
   */
  public async loadMoreSessions(): Promise<void> {
    if (!this.sessionsHasMore() || this.sessionsLoadingMore() || this.sessionsInFlight) return;
    this.sessionsLoadingMore.set(true);
    try {
      const page = await this.fetchSessionsPage(this.sessionsSkip);
      this.sessions.update((current) => [...current, ...page.sessions]);
      this.sessionsTotalCount = page.totalCount;
      this.sessionsSkip = this.sessions().length;
      this.sessionsHasMore.set(this.sessions().length < page.totalCount);
    } finally {
      this.sessionsLoadingMore.set(false);
    }
  }

  private async fetchSessionsPage(
    skip: number,
  ): Promise<{ sessions: ChatSession[]; totalCount: number }> {
    const search = this.sessionSearch().trim() || undefined;
    const result = await firstValueFrom(
      this.chatSessionsGql
        .fetch({ skip, take: SESSIONS_PAGE_SIZE, search }, { fetchPolicy: 'network-only' })
        .pipe(map((res) => res.data?.chatSessions)),
    );
    const sessions = (result?.nodes ?? []).map((node) => ({
      id: node.id,
      title: node.title ?? null,
      agentId: node.agentId ?? null,
      createdAt: node.createdAt,
      lastMessageAt: node.lastMessageAt ?? null,
    }));
    return { sessions, totalCount: result?.totalCount ?? sessions.length };
  }

  public async loadAgents(): Promise<void> {
    try {
      const nodes = await firstValueFrom(
        this.agentsGql
          .fetch({ skip: 0, take: 50 }, { fetchPolicy: 'network-only' })
          .pipe(map((res) => res.data?.agents.nodes ?? [])),
      );
      this.agents.set(
        nodes
          // Apenas agentes conversacionais (chat multi-turno); `headless` é one-shot.
          .filter(
            (node) => node.status === AgentStatusEnum.Active && node.kind === AgentKindEnum.Chat,
          )
          .map((node) => ({
            id: node.id,
            name: node.name,
            description: node.description ?? null,
            kind: node.kind ?? null,
          })),
      );
    } catch {
      // Usuário sem permissão de agentes / feature off → só "Iara Padrão".
      this.agents.set([]);
    }
  }

  public async openSession(sessionId: string): Promise<void> {
    if (sessionId === this.activeSessionId()) return;

    this.cancelStream();
    this.resetStreaming();
    this.pendingApprovals.set([]);
    this.actionStates.set({});
    this.activeSessionId.set(sessionId);
    this.activeAgentId.set(this.sessions().find((s) => s.id === sessionId)?.agentId ?? null);
    this.messages.set([]);
    this.isLoadingSession.set(true);

    try {
      const nodes = (await firstValueFrom(
        this.chatMessagesGql
          .fetch({ sessionId, skip: 0, take: 100 }, { fetchPolicy: 'network-only' })
          .pipe(map((res) => res.data?.chatMessages ?? [])),
      )) as RawMessageNode[];

      const last = nodes[nodes.length - 1];
      // `streaming` = geração ainda rodando no servidor (desacoplada da
      // subscription). Tratada como pendente: mostra o parcial salvo e re-assina
      // o stream para retomar o tail ao vivo (catch-up + continuação).
      const hasPendingAssistant =
        !!last &&
        last.role.toUpperCase() !== 'USER' &&
        (last.status === 'pending' || last.status === 'streaming');

      if (hasPendingAssistant) {
        this.messages.set(nodes.slice(0, -1).map(toChatMessage));
        this.accumulated = last.content ?? '';
        this.streamingText.set(this.accumulated);
        this.streamingMessageId = last.id;
        this.isStreaming.set(true);
        this.beginTurnTimer(last.createdAt ? new Date(last.createdAt) : new Date());
        this.subscribeToStream(last.id);
      } else {
        this.messages.set(nodes.map(toChatMessage));
      }
    } finally {
      this.isLoadingSession.set(false);
    }
  }

  public newConversation(agentId?: string | null): void {
    this.cancelStream();
    this.resetStreaming();
    this.pendingApprovals.set([]);
    this.actionStates.set({});
    this.activeSessionId.set(null);
    this.activeAgentId.set(agentId ?? null);
    this.messages.set([]);
  }

  /**
   * Agente escolhido para a próxima conversa nova. Vive no serviço (feature-scoped)
   * para sobreviver à recriação da `chat-page` na navegação entre rotas filhas.
   */
  private pendingAgentId: string | null = null;

  public setPendingAgent(agentId: string | null): void {
    this.pendingAgentId = agentId;
  }

  /** Inicia uma conversa nova consumindo o agente pendente (chamado pela rota `/chat`). */
  public startNewConversation(): void {
    this.newConversation(this.pendingAgentId);
    this.pendingAgentId = null;
  }

  // ── envio / regeneração ──────────────────────────────────────────────────────

  /**
   * @param context contexto da tela no momento do envio, usado pelo painel
   *   flutuante. A página `/chat` não passa nada.
   * @returns id da sessão recém-criada (conversa nova), ou `null`.
   */
  public async send(
    content: string,
    context?: SendMessageInput['context'],
  ): Promise<string | null> {
    const text = content.trim();
    if (!text || this.isStreaming()) return null;

    this.messages.update((current) => [...current, { role: 'USER', content: text }]);
    this.resetStreaming();
    this.isStreaming.set(true);
    this.beginTurnTimer(new Date());

    const wasNew = !this.activeSessionId();
    const input: SendMessageInput = {
      content: text,
      sessionId: this.activeSessionId() ?? undefined,
      agentId: this.activeAgentId() ?? undefined,
      context: context ?? undefined,
    };

    try {
      const result = await firstValueFrom(
        this.sendChatMessageGql.mutate({ input }).pipe(map((res) => res.data?.sendMessage ?? null)),
      );
      if (!result) {
        this.failStream();
        return null;
      }
      if (wasNew) this.activeSessionId.set(result.sessionId);
      this.streamingMessageId = result.id;
      this.subscribeToStream(result.id);
      return wasNew ? result.sessionId : null;
    } catch (error) {
      if (this.tryHandleCreditLimit(error)) {
        // Bloqueado no gate — a mensagem não foi criada no servidor; desfaz o append otimista.
        this.messages.update((current) => current.slice(0, -1));
        this.isStreaming.set(false);
        return null;
      }
      this.failStream();
      return null;
    }
  }

  /**
   * Regenera a resposta de um turno do modelo (ex.: após erro). Antes de mutar,
   * reconcilia com o servidor: um "failed" local pode ser só transporte — a
   * geração pode ter COMPLETADO (mostra o conteúdo real, sem regenerar) ou ainda
   * estar rodando (retoma o tail). Regenerar nesses casos descartaria uma
   * resposta boa e dispararia outro turno longo idêntico.
   *
   * EXCEÇÃO: turno interrompido pelo usuário (`metadata.stoppedByUser`) também
   * é `completed` no servidor, mas "Gerar novamente" ali é pedido EXPLÍCITO de
   * regeneração — o atalho de "só mostra o conteúdo" viraria um no-op (a bolha
   * re-commitada é idêntica à que já está na tela).
   */
  public async regenerate(messageId: string): Promise<void> {
    if (this.isStreaming()) return;

    const sessionId = this.activeSessionId();
    const fresh = sessionId ? await this.fetchMessageFromServer(sessionId, messageId) : null;

    const stoppedByUser =
      (fresh?.metadata as Record<string, unknown> | null | undefined)?.['stoppedByUser'] === true;

    if (fresh?.status === 'completed' && !stoppedByUser) {
      this.commitReconciledMessage(fresh);
      return;
    }

    if (fresh?.status === 'pending' || fresh?.status === 'streaming') {
      this.messages.update((current) => current.filter((message) => message.id !== messageId));
      this.resetStreaming();
      this.accumulated = fresh.content ?? '';
      this.streamingText.set(this.accumulated);
      this.streamingMessageId = messageId;
      this.isStreaming.set(true);
      this.beginTurnTimer(new Date(fresh.createdAt ?? Date.now()));
      this.subscribeToStream(messageId);
      return;
    }

    const index = this.messages().findIndex((message) => message.id === messageId);
    const messagesBeforeRegenerate = this.messages();
    if (index !== -1) this.messages.set(messagesBeforeRegenerate.slice(0, index));

    this.resetStreaming();
    this.isStreaming.set(true);
    this.beginTurnTimer(new Date());

    try {
      const result = await firstValueFrom(
        this.regenerateMessageGql
          .mutate({ messageId })
          .pipe(map((res) => res.data?.regenerateMessage ?? null)),
      );
      if (!result) {
        this.failStream();
        return;
      }
      this.streamingMessageId = result.id;
      this.subscribeToStream(result.id);
    } catch (error) {
      if (this.tryHandleCreditLimit(error)) {
        // Bloqueado por crédito: restaura as mensagens truncadas (a resposta anterior volta à tela).
        this.messages.set(messagesBeforeRegenerate);
        this.isStreaming.set(false);
        return;
      }
      this.failStream();
    }
  }

  /**
   * Detecta o erro de limite de créditos da Iara nos GraphQL errors da mutation e ativa o
   * estado de bloqueio com escopo + data de renovação. Retorna true quando tratou o erro.
   */
  private tryHandleCreditLimit(error: unknown): boolean {
    const graphQLErrors = (
      error as {
        graphQLErrors?: {
          extensions?: { code?: string; extra?: { renewalDate?: string; scope?: string } };
        }[];
      }
    )?.graphQLErrors;

    const match = graphQLErrors?.find(
      (item) =>
        item.extensions?.code === FluxErrorCodes.IaraCreditsLimitReached ||
        item.extensions?.code === FluxErrorCodes.IaraCreditsChannelLimitReached,
    );
    if (!match) return false;

    const extra = match.extensions?.extra;
    this.creditLimitInfo.set({
      renewalDate: extra?.renewalDate ?? null,
      scope:
        match.extensions?.code === FluxErrorCodes.IaraCreditsChannelLimitReached
          ? 'channel'
          : 'account',
    });
    this.creditLimitReached.set(true);
    return true;
  }

  /**
   * Dispara uma ação declarada por um componente:
   * - `follow_up`: re-prompta a Iara;
   * - `navigate`: abre a URL;
   * - `tool_call`: executa a tool MCP server-side (concorrência otimista). Ações
   *   destrutivas pedem confirmação; ações sensíveis voltam `queued_for_approval`
   *   e pausam para aprovação humana (a carta de aprovação chega pelo stream).
   */
  public async executeComponentAction(action: ChatAction, component: ChatComponent): Promise<void> {
    if (action.disabled) return;

    if (action.kind === 'follow_up' && action.prompt) {
      await this.send(action.prompt);
      return;
    }

    if (action.kind === 'navigate' && action.url) {
      window.open(action.url, action.target === 'new_tab' ? '_blank' : '_self');
      return;
    }

    if (action.kind === 'tool_call' && action.tool) {
      await this.runComponentToolCall(action, component);
    }
  }

  private async runComponentToolCall(action: ChatAction, component: ChatComponent): Promise<void> {
    // Já em execução, aguardando aprovação ou concluída → ignora reentrância.
    if (this.actionStates()[component.componentId]?.[action.id]) return;
    if (action.destructive && !(await this.confirmDestructiveAction(action))) return;

    this.setActionState(component.componentId, action.id, 'running');
    try {
      const result = await firstValueFrom(
        this.executeActionGql
          .mutate({
            input: {
              componentId: component.componentId,
              actionId: action.id,
              instanceVersion: component.instanceVersion,
              args: action.argsTemplate ?? {},
            },
          })
          .pipe(
            map(
              (res) =>
                (res.data?.executeChatComponentAction ?? null) as ChatComponentActionResult | null,
            ),
          ),
      );

      switch (result?.outcome) {
        case 'ok':
          this.setActionState(component.componentId, action.id, 'done');
          this.toast.showSuccess('Ação executada.');
          break;
        case 'queued_for_approval':
          this.setActionState(component.componentId, action.id, 'queued');
          this.toast.showInfo('Ação enviada para aprovação.');
          break;
        default:
          this.clearActionState(component.componentId, action.id);
          this.toast.showError(result?.errorMessage ?? 'Não foi possível executar a ação.');
      }
    } catch {
      this.clearActionState(component.componentId, action.id);
      this.toast.showError('Não foi possível executar a ação.');
    }
  }

  private async confirmDestructiveAction(action: ChatAction): Promise<boolean> {
    const dialog = this.confirmDialog.openDialog({
      title: action.label,
      text: 'Esta ação altera dados da sua operação e não pode ser desfeita. Deseja continuar?',
      successText: action.label,
      successVariant: 'negative',
      cancelText: 'Cancelar',
    });
    const result = await dialog.afterClosed<{ role: 'cancel' | 'success' }>();
    return result?.role === 'success';
  }

  private setActionState(componentId: string, actionId: string, status: ChatActionStatus): void {
    this.actionStates.update((states) => ({
      ...states,
      [componentId]: { ...(states[componentId] ?? {}), [actionId]: status },
    }));
  }

  private clearActionState(componentId: string, actionId: string): void {
    this.actionStates.update((states) => {
      const forComponent = { ...(states[componentId] ?? {}) };
      delete forComponent[actionId];
      return { ...states, [componentId]: forComponent };
    });
  }

  // ── sessões ──────────────────────────────────────────────────────────────────

  public async renameSession(sessionId: string, title: string): Promise<void> {
    try {
      await firstValueFrom(this.renameChatSessionGql.mutate({ input: { sessionId, title } }));
      this.sessions.update((current) =>
        current.map((session) => (session.id === sessionId ? { ...session, title } : session)),
      );
      this.toast.showSuccess('Conversa renomeada.');
    } catch {
      this.toast.showError('Não foi possível renomear a conversa.');
    }
  }

  public async deleteSession(sessionId: string): Promise<void> {
    try {
      await firstValueFrom(this.deleteChatSessionGql.mutate({ sessionId }));
      this.sessions.update((current) => current.filter((session) => session.id !== sessionId));
      if (this.activeSessionId() === sessionId) this.newConversation();
      this.toast.showSuccess('Conversa excluída.');
    } catch {
      this.toast.showError('Não foi possível excluir a conversa.');
    }
  }

  /** Encerra a sessão no backend (libera o runtime do agente). Best-effort. */
  public async endSession(sessionId: string): Promise<void> {
    await firstValueFrom(this.endChatSessionGql.mutate({ sessionId })).catch(() => undefined);
  }

  // ── aprovação humana ─────────────────────────────────────────────────────────

  public async approve(requestId: string, note?: string): Promise<void> {
    this.markApproval(requestId, 'approved');
    await firstValueFrom(this.approveGql.mutate({ input: { id: requestId, note } })).catch(() =>
      this.markApproval(requestId, 'pending'),
    );
  }

  public async reject(requestId: string, note?: string): Promise<void> {
    this.markApproval(requestId, 'rejected');
    await firstValueFrom(this.rejectGql.mutate({ input: { id: requestId, note } })).catch(() =>
      this.markApproval(requestId, 'pending'),
    );
  }

  private subscribeToApprovals(): void {
    this.approvalsSub = this.approvalsStreamGql
      .subscribe({}, { fetchPolicy: 'no-cache' })
      .subscribe({
        next: (res) => {
          const event = res.data?.agentApprovalsStream;
          if (event) this.handleApprovalEvent(event);
        },
        error: () => undefined,
      });
  }

  private handleApprovalEvent(event: ApprovalStreamEvent): void {
    const approval = event.approval;
    if (approval.chatSessionId && approval.chatSessionId !== this.activeSessionId()) return;

    if (event.kind === AgentApprovalEventKind.Resolved) {
      this.pendingApprovals.update((list) => list.filter((a) => a.requestId !== approval.id));
      if (event.resumeChatMessageId) {
        this.streamingMessageId = event.resumeChatMessageId;
        this.resetStreaming();
        this.isStreaming.set(true);
        this.beginTurnTimer(new Date());
        this.subscribeToStream(event.resumeChatMessageId);
      }
      return;
    }

    // kind === 'created'
    this.upsertApproval({
      requestId: approval.id,
      messageId: approval.chatMessageId ?? undefined,
      sessionId: approval.chatSessionId ?? undefined,
      toolName: approval.toolName,
      toolArgs: approval.toolArgs,
      rationale: approval.rationale,
      status: 'pending',
    });
  }

  // ── streaming ────────────────────────────────────────────────────────────────

  private subscribeToStream(messageId: string): void {
    this.cancelStream();
    this.receivedTerminal = false;
    this.streamSub = this.chatStreamGql
      .subscribe({ messageId }, { fetchPolicy: 'no-cache' })
      .subscribe({
        next: (res) => {
          const event = res.data?.chatStream;
          if (!event) return;
          // Stream vivo: qualquer evento (catch-up incluso) zera o contador de
          // reconexão — só transporte que falha SEM entregar nada esgota o limite.
          this.reconnectAttempts = 0;
          this.applyStreamEvent(event);
        },
        // Erro de transporte NÃO é veredito: a geração continua server-side
        // (desacoplada da subscription). Reconcilia com o estado persistido em
        // vez de marcar failed — o servidor é a fonte da verdade.
        error: () => void this.reconcileStream(messageId, true),
        complete: () => {
          if (!this.receivedTerminal) void this.reconcileStream(messageId, false);
        },
      });

    // chat-over-socket (v2): além do Apollo, consome os frames do socket e
    // mergeia por `seq` (first-wins) na mesma `applyStreamEvent`. Gated pela
    // flag `chat:over-socket`; no-op quando off.
    this.subscribeToSocketStream(messageId);
  }

  /**
   * Aplica um frame de stream de QUALQUER fonte (Apollo ou socket). As duas
   * fontes carregam o MESMO `seq` por frame; aplicamos só `seq` estritamente
   * crescente (`lastAppliedSeq`), o que dedupa E descarta frames atrasados de
   * `seq` menor — sem isso um token de `content` absoluto chegando fora de ordem
   * rolaria o texto para trás. Frames sem `seq` (replay/reconcile do servidor)
   * são sempre aplicados. Marca o terminal independentemente da fonte.
   */
  private applyStreamEvent(event: ChatStreamEvent): void {
    const seq = (event as { seq?: number | null }).seq;
    if (typeof seq === 'number') {
      // Estritamente crescente: dedupa + descarta frames atrasados (seq menor).
      if (seq <= this.lastAppliedSeq) return;
      this.lastAppliedSeq = seq;
    }

    if (event.__typename === 'ChatDoneEventEntity' || event.__typename === 'ChatErrorEventEntity') {
      this.receivedTerminal = true;
    }

    this.handleStreamEvent(event);
  }

  /**
   * Assina a fluid v2 por-mensagem no socket e encaminha cada frame de chat para
   * `applyStreamEvent`. A fluid é user-scoped (`...:user:<owner>:chat-session:<s>:
   * chat-message:<m>`), autorizada pelo guard de user-match do socket sem
   * consulta ao banco. No-op quando a flag está off ou falta contexto.
   */
  private subscribeToSocketStream(messageId: string): void {
    if (!this.featureFlagService.isFeatureFlagEnabledSync(FeatureFlagsEnum.ChatOverSocket)) {
      return;
    }

    const accountId = this.authService.getAccountId();
    const userId = this.authService.authenticatedUser()?.user?.id;
    const sessionId = this.activeSessionId();
    if (!accountId || !userId || !sessionId) return;

    // String crua: o client só tem o FluidBuilder v1 (@flux/core); o cast no
    // boundary da API v1-tipada é deliberado (a entrega no socket é agnóstica de
    // versão e a autorização v2/chat acontece no servidor).
    const fluid =
      `fluid:v2:account:${accountId}:user:${userId}:chat-session:${sessionId}:chat-message:${messageId}` as FluidPrefixType;
    this.realtimeService.subscribe(fluid);
    this.subscribedMessageFluid = fluid;

    this.socketStreamSub = this.realtimeService.chatEventReceived$.subscribe((frame) => {
      const event = this.mapSocketStreamFrame(frame.op, frame.data, messageId);
      if (event) this.applyStreamEvent(event);
    });
  }

  /**
   * Converte um frame cru de chat v2 (`{ op, data }`) do socket para o shape
   * `ChatStreamEvent` que o `handleStreamEvent` consome — mapeando os campos
   * snake_case do payload `@flux/schemas` para o camelCase do entity GraphQL.
   * Retorna `null` para ops que não são frames de stream da mensagem em
   * andamento (`data.id` precisa casar com `messageId`).
   */
  private mapSocketStreamFrame(
    op: string,
    raw: unknown,
    messageId: string,
  ): ChatStreamEvent | null {
    const data = raw as Record<string, unknown>;
    if (!data || data['id'] !== messageId) return null;

    const base = { messageId: data['id'], seq: data['seq'] };
    switch (op) {
      case 'chat:stream:token-emitted':
        return {
          __typename: 'ChatTokenEventEntity',
          type: 'token',
          ...base,
          text: data['text'],
          content: data['content'],
        } as unknown as ChatStreamEvent;
      case 'chat:stream:reasoned':
        return {
          __typename: 'ChatReasoningEventEntity',
          type: 'reasoning',
          ...base,
          text: data['text'],
          parentTool: data['parent_tool'],
        } as unknown as ChatStreamEvent;
      case 'chat:stream:tool-started':
        return {
          __typename: 'ChatToolStartEventEntity',
          type: 'tool_start',
          ...base,
          name: data['name'],
          title: data['title'],
          parentTool: data['parent_tool'],
        } as unknown as ChatStreamEvent;
      case 'chat:stream:tool-ended':
        return {
          __typename: 'ChatToolEndEventEntity',
          type: 'tool_end',
          ...base,
          name: data['name'],
          title: data['title'],
          output: data['output'],
          parentTool: data['parent_tool'],
        } as unknown as ChatStreamEvent;
      case 'chat:stream:component-emitted':
        return {
          __typename: 'ChatComponentEventEntity',
          type: 'component',
          ...base,
          componentEnvelope: data['envelope'],
        } as unknown as ChatStreamEvent;
      case 'chat:stream:approval-requested':
        return {
          __typename: 'ChatApprovalRequestedEventEntity',
          type: 'approval-requested',
          ...base,
          requestId: data['request_id'],
          toolName: data['tool_name'],
          toolArgs: data['tool_args'],
          rationale: data['rationale'],
        } as unknown as ChatStreamEvent;
      case 'chat:stream:completed':
        return {
          __typename: 'ChatDoneEventEntity',
          type: 'done',
          ...base,
          content: data['content'],
          stopReason: data['stop_reason'],
          pendingApprovals: data['pending_approvals'] ?? [],
          citations: data['citations'] ?? [],
        } as unknown as ChatStreamEvent;
      case 'chat:stream:failed':
        return {
          __typename: 'ChatErrorEventEntity',
          type: 'error',
          ...base,
          message: data['message'],
          code: data['code'],
          retryable: data['retryable'],
          metadata: data['metadata'],
        } as unknown as ChatStreamEvent;
      default:
        return null;
    }
  }

  /**
   * Reconcilia um stream que terminou sem evento terminal (queda de WS, deploy,
   * fim silencioso) com o estado persistido no servidor:
   * - `completed` → commita o conteúdo real (a geração terminou bem; o erro era
   *   só do transporte);
   * - `failed` → materializa o turno falho (retryable);
   * - `pending`/`streaming` → re-assina o stream (replay server-side é
   *   idempotente: token absoluto + catch-up), com backoff e limite de
   *   tentativas sem progresso;
   * - não encontrada / refetch falhou → falha se veio de erro, senão só limpa.
   */
  private async reconcileStream(messageId: string, fromError: boolean): Promise<void> {
    if (this.streamingMessageId !== messageId) return;

    const sessionId = this.activeSessionId();
    if (!sessionId) {
      this.failStream('A resposta foi interrompida. Tente novamente.');
      return;
    }

    const fresh = await this.fetchMessageFromServer(sessionId, messageId);
    // Usuário navegou/trocou de turno durante o refetch → estado já foi limpo.
    if (this.streamingMessageId !== messageId) return;

    if (fresh?.status === 'completed') {
      this.commitReconciledMessage(fresh);
      return;
    }

    if (fresh?.status === 'pending' || fresh?.status === 'streaming') {
      this.scheduleResubscribe(messageId);
      return;
    }

    if (fresh?.status === 'failed' || fromError) {
      this.failStream('A resposta foi interrompida. Tente novamente.');
      return;
    }

    this.resetStreaming();
  }

  private async fetchMessageFromServer(
    sessionId: string,
    messageId: string,
  ): Promise<RawMessageNode | null> {
    try {
      const nodes = (await firstValueFrom(
        this.chatMessagesGql
          .fetch({ sessionId, skip: 0, take: 100 }, { fetchPolicy: 'network-only' })
          .pipe(map((res) => res.data?.chatMessages ?? [])),
      )) as RawMessageNode[];
      return nodes.find((node) => node.id === messageId) ?? null;
    } catch {
      return null;
    }
  }

  /** Substitui (nunca duplica) o turno em andamento pela mensagem persistida. */
  private commitReconciledMessage(fresh: RawMessageNode): void {
    this.messages.update((current) => [
      ...current.filter((message) => message.id !== fresh.id),
      toChatMessage(fresh),
    ]);
    this.resetStreaming();
    this.cancelStream();
    void this.loadSessions();
  }

  private scheduleResubscribe(messageId: string): void {
    if (this.reconnectAttempts >= MAX_STREAM_RECONNECTS) {
      this.failStream('A resposta foi interrompida. Tente novamente.');
      return;
    }

    this.reconnectAttempts += 1;
    const backoff = RECONNECT_BASE_DELAY_MS * 2 ** (this.reconnectAttempts - 1);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // Revalida: o usuário pode ter navegado durante a espera.
      if (this.streamingMessageId !== messageId) return;
      this.subscribeToStream(messageId);
    }, backoff);
  }

  private handleStreamEvent(event: ChatStreamEvent): void {
    switch (event.__typename) {
      case 'ChatTokenEventEntity': {
        this.accumulated = event.content ?? this.accumulated + (event.text ?? '');
        this.streamingText.set(this.accumulated);
        break;
      }
      case 'ChatReasoningEventEntity': {
        this.accumulatedReasoning += event.text ?? '';
        this.streamingReasoning.set(this.accumulatedReasoning);
        break;
      }
      case 'ChatToolStartEventEntity': {
        this.thinkingSteps = [
          ...this.thinkingSteps,
          {
            id: `${event.name}:${this.thinkingSteps.length}`,
            status: 'running',
            title: event.title ?? humanizeToolName(event.name),
            // Eventos com `parentTool` vêm de um sub-agente (query_data/
            // mutate_data) e são exibidos recuados sob o passo pai.
            ...(event.parentTool ? { depth: 1 } : {}),
          },
        ];
        this.streamingThinking.set(this.thinkingSteps);
        break;
      }
      case 'ChatToolEndEventEntity': {
        this.finishLastRunningStep(event.title ?? undefined, event.output ?? undefined);
        break;
      }
      case 'ChatComponentEventEntity': {
        if (event.componentEnvelope) {
          const component = toComponent(event.componentEnvelope as unknown as RawComponent);
          this.streamingComponents.set(component.componentId, component);
        }
        break;
      }
      case 'ChatApprovalRequestedEventEntity': {
        if (event.requestId) {
          this.upsertApproval({
            requestId: event.requestId,
            messageId: event.messageId,
            sessionId: this.activeSessionId() ?? undefined,
            toolName: event.toolName,
            toolArgs: event.toolArgs,
            rationale: event.rationale,
            status: 'pending',
          });
        }
        break;
      }
      case 'ChatDoneEventEntity': {
        const content = sanitizeAssistantChatContent((event.content ?? this.accumulated).trim());
        const citations = (event.citations ?? []).map(toCitation);
        this.commitModelMessage(content, citations);
        void this.loadSessions();
        break;
      }
      case 'ChatErrorEventEntity': {
        this.failStream(event.message ?? 'Houve um erro ao gerar a resposta.', event.retryable);
        break;
      }
      default:
        break;
    }
  }

  private commitModelMessage(
    content: string,
    citations: ChatCitation[],
    options: { failed?: boolean; retryable?: boolean } = {},
  ): void {
    const thinking = this.thinkingSteps.length ? this.thinkingSteps : undefined;
    const reasoning = this.accumulatedReasoning.length ? this.accumulatedReasoning : undefined;
    const components = this.streamingComponents.size
      ? [...this.streamingComponents.values()]
      : undefined;

    // Sempre materializa o turno concluído. Um turno sem conteúdo/componentes/
    // citações (e não-falho) representa "resposta sem valor" e é renderizado como
    // o callout "Nenhum resultado encontrado" — em vez de sumir silenciosamente.
    this.messages.update((current) => [
      ...current,
      {
        role: 'MODEL',
        content,
        ...(this.streamingMessageId ? { id: this.streamingMessageId } : {}),
        ...(thinking ? { thinking } : {}),
        ...(reasoning ? { reasoning } : {}),
        ...(components ? { components } : {}),
        ...(citations.length ? { citations } : {}),
        ...(options.failed ? { failed: true, retryable: options.retryable ?? true } : {}),
      },
    ]);

    this.resetStreaming();
    this.isStreaming.set(false);
    this.cancelStream();
  }

  private failStream(
    message = 'Não foi possível gerar a resposta. Tente novamente.',
    retryable = true,
  ): void {
    const content = this.accumulated.trim() || sanitizeAssistantChatContent(message);
    this.commitModelMessage(content, [], { failed: true, retryable });
  }

  // ── helpers ──────────────────────────────────────────────────────────────────

  private upsertApproval(approval: ChatApproval): void {
    this.pendingApprovals.update((list) =>
      list.some((a) => a.requestId === approval.requestId) ? list : [...list, approval],
    );
  }

  private markApproval(requestId: string, status: ChatApproval['status']): void {
    this.pendingApprovals.update((list) =>
      list.map((a) => (a.requestId === requestId ? { ...a, status } : a)),
    );
  }

  private finishLastRunningStep(title?: string, output?: string): void {
    for (let index = this.thinkingSteps.length - 1; index >= 0; index -= 1) {
      if (this.thinkingSteps[index].status === 'running') {
        this.thinkingSteps = this.thinkingSteps.map((step, i) =>
          i === index
            ? { ...step, status: 'done', title: title ?? step.title, output: output ?? step.output }
            : step,
        );
        this.streamingThinking.set(this.thinkingSteps);
        return;
      }
    }
  }

  private cancelStream(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = null;
    this.socketStreamSub?.unsubscribe();
    this.socketStreamSub = null;
    if (this.subscribedMessageFluid) {
      this.realtimeService.unsubscribe(this.subscribedMessageFluid);
      this.subscribedMessageFluid = null;
    }
    this.lastAppliedSeq = -1;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Liga cronômetro + agenda o hint, respeitando tempo já decorrido (resume). */
  private beginTurnTimer(startedAt: Date): void {
    this.turnStartedAt.set(startedAt);
    this.clearLongTurnHint();
    const remaining = LONG_TURN_HINT_AFTER_MS - (Date.now() - startedAt.getTime());
    if (remaining <= 0) {
      this.longTurnHintVisible.set(true);
      return;
    }
    this.longTurnHintTimer = setTimeout(() => this.longTurnHintVisible.set(true), remaining);
  }

  private clearLongTurnHint(): void {
    if (this.longTurnHintTimer !== null) {
      clearTimeout(this.longTurnHintTimer);
      this.longTurnHintTimer = null;
    }
    this.longTurnHintVisible.set(false);
  }

  /**
   * Interrompe a geração mantendo o parcial. A mutation devolve a mensagem
   * terminal (stoppedByUser no metadata) — commitamos direto; se falhar, o
   * stream/fencing ainda resolve o turno (melhor-esforço).
   */
  public async stop(): Promise<void> {
    const messageId = this.streamingMessageId;
    if (!messageId) return;
    try {
      const result = await firstValueFrom(
        this.stopMessageGql.mutate({ messageId }).pipe(map((res) => res.data?.stopMessage ?? null)),
      );
      if (this.streamingMessageId !== messageId) return; // usuário navegou durante a mutation
      if (!result) return;
      this.commitReconciledMessage(result as RawMessageNode);
    } catch {
      // mantém o streaming; o terminal chega pelo stream ou pela reconciliação
    }
  }

  private resetStreaming(): void {
    this.isStreaming.set(false);
    this.accumulated = '';
    this.accumulatedReasoning = '';
    this.thinkingSteps = [];
    this.streamingComponents.clear();
    this.streamingMessageId = null;
    this.receivedTerminal = false;
    this.reconnectAttempts = 0;
    this.streamingText.set('');
    this.streamingThinking.set([]);
    this.streamingReasoning.set('');
    this.turnStartedAt.set(null);
    this.clearLongTurnHint();
  }
}

function toChatMessage(node: RawMessageNode): ChatMessage {
  const role = node.role.toUpperCase() === 'USER' ? 'USER' : 'MODEL';
  const trail = role === 'MODEL' ? readReasoningTrail(node.metadata) : null;
  const stopped =
    role === 'MODEL' &&
    (node.metadata as Record<string, unknown> | null | undefined)?.['stoppedByUser'] === true;
  return {
    id: node.id,
    role,
    content: role === 'MODEL' ? sanitizeAssistantChatContent(node.content) : node.content,
    ...(trail?.steps.length ? { thinking: trail.steps } : {}),
    ...(trail?.reasoning ? { reasoning: trail.reasoning } : {}),
    ...(node.sources?.length ? { citations: node.sources.map(toCitation) } : {}),
    ...(node.components?.length ? { components: node.components.map(toComponent) } : {}),
    ...(stopped ? { stopped: true } : {}),
  };
}

function toComponent(raw: RawComponent): ChatComponent {
  return {
    componentId: raw.componentId,
    componentType: raw.componentType,
    instanceVersion: raw.instanceVersion,
    generatedAt: raw.generatedAt ?? undefined,
    data: raw.data,
    presentation: raw.presentation,
    // Ações vêm do nosso backend (manifest) — shape confiável; narrowing no host.
    actions: (raw.actions ?? []) as ChatAction[],
    fallbackText: raw.fallbackText,
  };
}

function toCitation(source: RawSource): ChatCitation {
  return {
    id: source.id,
    title: source.title ?? null,
    uri: source.uri ?? null,
    sourceType: source.sourceType ?? null,
    score: source.score ?? null,
  };
}

function humanizeToolName(name: string): string {
  return name
    .replaceAll('_', ' ')
    .replaceAll(/\s+/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}
